// @greenlight-spec: 16-api-conventions.md
// @greenlight-spec: 17-code-organization.md
// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md
import { z } from 'zod';
import { AppInactivityNoticeSchema } from '../types/app-lifecycle.js';
import { APP_ACCESS_ROLES, APP_SORT_KEYS, APP_STATUSES, APP_TYPES, FLEET_BUCKETS, SORT_DIRECTIONS, } from '../types/app.js';
import { RolloutBlockerSchema } from '../types/rollout-blocker.js';
import { AgentKindSchema } from '../types/install.js';
import { CLOUD_PROVIDERS, EMAIL_TRANSPORTS } from '../types/providers.js';
import { INTEGRATION_APPROVAL_MODES, INTEGRATION_AUTH_MODES, INTEGRATION_STATUSES, PERMISSION_STATUSES, PRINCIPAL_TYPES, USER_DELEGATED_MECHANISMS, USER_CONNECTION_STATUSES, } from '../types/integration.js';
import { PIPELINE_RUN_STATUSES, } from '../types/pipeline.js';
import { GROUP_SOURCES, ORG_ROLES, ROLE_SOURCES, } from '../types/user.js';
import { FEEDBACK_CAPS, FEEDBACK_CATEGORIES, FEEDBACK_FINGERPRINT_PATTERN, FEEDBACK_ORIGINS, FEEDBACK_SOURCES, FEEDBACK_STATUSES, FEEDBACK_SURFACES, } from '../types/feedback.js';
import { APP_SLUG_REGEX } from '../constants/index.js';
import { AGENT_USAGE_VARIANTS } from '../constants/agent-model-prices.js';
/** Why a configured notification email sender failed, as Settings names it. */
export const EMAIL_FAILURE_REASONS = [
    'password_missing',
    'sign_in_refused',
    'relay_unreachable',
    'send_failed',
];
const isoDateTime = () => z.string().datetime({ offset: true });
// Canonical error response shape — see 16-api-conventions.md §Error Response Shape.
// Every non-2xx response (REST, MCP, proxy, AI gateway, webhook) uses this body.
export const GreenlightErrorResponseSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    next_steps: z.string().optional(),
    request_id: z.string(),
});
// Opaque cursor pagination — see 16-api-conventions.md §Pagination.
export const PaginationQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
/**
 * Apps-list query params.
 *
 * Every filter here is applied as SQL, so a filtered view reflects the whole org fleet
 * rather than whichever page happened to load. They are dashboard-only in practice — MCP's
 * `listApps` tool sends none of them.
 */
export const AppsListQuerySchema = PaginationQuerySchema.extend({
    /**
     * Whether the caller's hidden apps are part of the result set. Explicit literals rather
     * than `z.coerce.boolean()`, which would read `include_hidden=false` as true.
     */
    include_hidden: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    /** Exact lifecycle status. */
    status: z.enum(APP_STATUSES).optional(),
    /** Status group; composes with `status`, and both may be supplied. */
    bucket: z.enum(FLEET_BUCKETS).optional(),
    /**
     * Narrow to apps whose **latest** pipeline run failed. An app can be `active` and
     * serving on its last good image while its newest run is red; the `needs_attention`
     * bucket includes these apps too.
     */
    pipeline: z.enum(['failed']).optional(),
    /**
     * Tag filter, either repeated (`?tag=a&tag=b`) or comma-separated (`?tag=a,b`).
     * `tag_mode` decides whether an app must carry any of them (default) or all of
     * them; matching is case-insensitive. A tag containing a comma is only
     * addressable through the repeated form.
     */
    tag: z
        .union([z.string().min(1), z.array(z.string().min(1))])
        .transform((v) => (Array.isArray(v) ? v : v.split(',')).map((t) => t.trim()).filter((t) => t.length > 0))
        .optional(),
    tag_mode: z.enum(['any', 'all']).optional(),
    /** Case-insensitive substring over slug and name. */
    q: z.string().min(1).max(200).optional(),
    /**
     * Orders the whole result set, not the page. A cursor is bound to the sort that issued it,
     * so changing `sort`/`order` mid-walk is rejected as `validation.cursor_invalid` rather
     * than silently resuming at a position that no longer means anything.
     */
    sort: z.enum(APP_SORT_KEYS).optional(),
    order: z.enum(SORT_DIRECTIONS).optional(),
});
export const paginated = (item) => z.object({
    items: z.array(item),
    next_cursor: z.string().nullable(),
});
export const AppTypeSchema = z.enum(APP_TYPES);
export const AppStatusSchema = z.enum(APP_STATUSES);
export const OrgRoleSchema = z.enum(ORG_ROLES);
export const RoleSourceSchema = z.enum(ROLE_SOURCES);
export const GroupSourceSchema = z.enum(GROUP_SOURCES);
export const PipelineRunStatusSchema = z.enum(PIPELINE_RUN_STATUSES);
export const IntegrationAuthModeSchema = z.enum(INTEGRATION_AUTH_MODES);
export const IntegrationApprovalModeSchema = z.enum(INTEGRATION_APPROVAL_MODES);
export const IntegrationStatusSchema = z.enum(INTEGRATION_STATUSES);
export const PermissionStatusSchema = z.enum(PERMISSION_STATUSES);
export const AppAccessRoleSchema = z.enum(APP_ACCESS_ROLES);
export const AppSlugSchema = z.string().regex(APP_SLUG_REGEX, {
    message: "Slug doesn't match ^[a-z][a-z0-9-]{1,40}[a-z0-9]$",
});
export const AppSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().uuid(),
    ownerId: z.string().uuid(),
    slug: AppSlugSchema,
    servingSubdomain: z.string().nullable(),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    type: AppTypeSchema,
    status: AppStatusSchema,
    repoFullName: z.string().max(255).nullable(),
    deploymentUrl: z.string().url().nullable(),
    isDraft: z.boolean(),
    accessRestricted: z.boolean(),
    docSummary: z.string().nullable(),
    docPurpose: z.string().nullable(),
    docArchitecture: z.string().nullable(),
    createdAt: isoDateTime(),
    updatedAt: isoDateTime(),
});
// Read-side response shapes — the REST mirror of the MCP `listApps` / `getApp`
// tools (see 06-agent-interface.md). Both REST and MCP delegate to the same
// read-side app service, so these schemas track the service's snake_case output
// (which the flat AppSchema above, used by the write side, cannot represent —
// it has no shared-environment summary and the read service never selects
// orgId/ownerId/deploymentUrl/doc* columns).
// One row in the apps list — the app's stable summary fields plus the
// denormalized governance/health fields the dashboard catalog surfaces.
export const AppListItemSchema = z.object({
    app_id: z.string().uuid(),
    slug: z.string(),
    /** Editable public address under the apps domain; null = serves at the slug (ADR-0050). */
    serving_subdomain: z.string().nullable().optional(),
    name: z.string(),
    type: z.string(),
    status: z.string(),
    repo_full_name: z.string().nullable(),
    created_at: z.string(),
    /** Owner's email; null if the owner row is missing. */
    owner_email: z.string().nullable(),
    /** Owner's display name (users.display_name); null when unset or the owner row is missing. */
    owner_name: z.string().nullable(),
    /** Timestamp of the last successful deploy (passed pipeline run); null if never deployed. */
    last_deploy_at: z.string().nullable(),
    /** Status of the most recent pipeline run; null if no runs yet. */
    last_pipeline_status: z.string().nullable(),
    /** Owner-supplied app icon URL; null/absent → UI renders a monogram. */
    icon_url: z.string().nullable().optional(),
    /**
     * Signed URL of the app's first captured screenshot — the cover the Grid view
     * renders (docs/28 §/apps View modes). The full set stays on the detail read
     * (GetAppResult.screenshot_urls); only the cover travels with the list so the
     * catalog can be screenshot-forward without an N× detail fan-out. Null/absent
     * → the card shows a placeholder frame.
     */
    cover_screenshot_url: z.string().nullable().optional(),
    /** Integration slugs this app uses (placeholder until integrations↔apps m2m read-side lands). */
    integrations: z.array(z.string()).optional(),
    /**
     * Org-wide category tags declared in `greenlight.yml` and projected on deploy
     * (ADR-0032). Powers the `/apps` catalog tag filter; empty until first deploy.
     */
    tags: z.array(z.string()).optional(),
    /**
     * The caller's own apps-list personalization (T-7557) — favorites sort
     * above the rest, hidden apps are excluded by default. Personal view state
     * only, no governance meaning. Optional/absent for callers that don't
     * request personalization (e.g. MCP `listApps`, which never sets it).
     */
    favorite: z.boolean().optional(),
    hidden: z.boolean().optional(),
    /**
     * The inactivity scan has flagged this app and the org's auto-pause step will
     * pause it unless it is used, confirmed as still needed, or snoozed
     * (docs/11 §Zombie App Detection). Derived at read time from the app's
     * lifecycle row and the org's thresholds, so an exempt, snoozed app, or an org
     * that turned auto-pause off, reads false. Absent for callers that don't
     * request personalization (MCP `listApps`).
     */
    scheduled_to_pause: z.boolean().optional(),
});
// The caller's own favorite/hidden state for one app (T-7557) — returned by
// the favorite/hidden toggle endpoints so the UI can trust the response
// directly rather than re-fetching the list.
export const AppUserPrefsSchema = z.object({
    app_id: z.string().uuid(),
    favorite: z.boolean(),
    hidden: z.boolean(),
});
// One row in the marketplace discovery read (ADR-0038, docs/42; T-533/T-6173).
// The catalog-safe contract (docs/42 §Access requests): a row the viewer
// cannot open still carries id/slug/name/owner/tags/doc_summary/icon_url, but
// `deployment_url` and `cover_screenshot_url` are always null (never the
// original bytes) — the blurred-derivative pipeline is T-5194, so a restricted
// row's cover is null until that lands, per docs/42 §Blurred previews ("no
// derivative exists" renders the icon/monogram, never a fallback to original).
export const MarketplaceAppSchema = z.object({
    app_id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    doc_summary: z.string().nullable(),
    owner_email: z.string().nullable(),
    owner_name: z.string().nullable(),
    tags: z.array(z.string()),
    icon_url: z.string().nullable(),
    cover_screenshot_url: z.string().nullable(),
    deployment_url: z.string().nullable(),
    created_at: z.string(),
    /**
     * True when the app is restricted to the people named on it. Only ever `true`
     * for the owner, its editors, and IT — a restricted row is not returned to
     * anyone else at all, so it reads `false` for them.
     */
    unlisted: z.boolean(),
    /**
     * The caller's own view state for this card (`app_user_prefs`, T-7557). Personal
     * and unaudited: `hidden` means "I don't want to see this card", never that the
     * app is unlisted (`unlisted` above) or that access changed. Optional like
     * `/apps`': the dashboard read always personalizes, MCP's `discoverApps`
     * never does and omits both keys rather than advertising a flag that is
     * always false.
     */
    favorite: z.boolean().optional(),
    hidden: z.boolean().optional(),
});
/** Same shape `/apps` uses, so Show-hidden behaves identically on both surfaces. */
export const MarketplaceListQuerySchema = PaginationQuerySchema.extend({
    include_hidden: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
// ── Marketplace access requests (docs/42 §Access requests; T-6173) ─────────
export const AppAccessGrantSchema = z.object({
    id: z.string().uuid(),
    app_id: z.string().uuid(),
    user_id: z.string().uuid(),
    user_email: z.string(),
    user_name: z.string().nullable(),
    status: PermissionStatusSchema,
    role: AppAccessRoleSchema,
    reason: z.string().nullable(),
    requested_at: z.string(),
    reviewed_at: z.string().nullable(),
    reviewed_by: z.string().uuid().nullable(),
    reviewed_by_email: z.string().nullable(),
    reviewed_by_name: z.string().nullable(),
    review_note: z.string().nullable(),
});
export const CreateAppAccessRequestSchema = z.object({
    reason: z.string().max(500).optional(),
});
export const ReviewAppAccessGrantSchema = z.object({
    reason: z.string().max(500).optional(),
});
/**
 * An owner naming someone who should have access, before that person asks.
 *
 * By email rather than user id because that is what an owner knows — they are
 * naming a colleague, not picking a row. The email must resolve to an existing
 * user in the org: Greenlight does not invite, and silently accepting an address
 * that matches nobody would leave an owner believing they granted access.
 */
export const GrantAppAccessByEmailSchema = z.object({
    email: z.string().email(),
    reason: z.string().max(500).optional(),
});
// Org-wide app counts grouped by lifecycle status, powering the apps-inventory
// summary cards. Counts span every app in the caller's org, so they cannot be
// derived from the cursor-paginated list (which only ever holds one page).
export const AppStatusCountsSchema = z.object({
    registering: z.number().int().nonnegative(),
    registered: z.number().int().nonnegative(),
    building: z.number().int().nonnegative(),
    deploying: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    degraded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    suspended: z.number().int().nonnegative(),
    queued_for_deletion: z.number().int().nonnegative(),
    deleted: z.number().int().nonnegative(),
});
/**
 * Who to ask for access to an app you cannot open.
 *
 * Deliberately three fields and no more. The viewer has been refused this app, so
 * this read exists to name the human who can grant it — not to disclose the state
 * (status, URLs, tags, pipeline) the refusal is meant to withhold.
 */
export const AppOwnerContactSchema = z.object({
    app_name: z.string(),
    owner_name: z.string().nullable(),
    owner_email: z.string(),
});
export const AppSummarySchema = z.object({
    /** Every app in the org, regardless of status. */
    total: z.number().int().nonnegative(),
    by_status: AppStatusCountsSchema,
    /**
     * Distinct apps that are failed or degraded, or live with a failing latest pipeline
     * run — the `needs_attention` bucket, so an app in both sets counts once.
     */
    needs_attention: z.number().int().nonnegative(),
    /** Successful default-branch deploys across the org over the trailing 7 days. */
    deploys_last_7_days: z.number().int().nonnegative(),
    // Non-deleted apps whose latest pipeline run failed, scoped to what the
    // caller can list (owner/co-owner for the `user` role). `apps` is capped;
    // `total` is the real count.
    pipeline_failures: z.object({
        total: z.number().int().nonnegative(),
        apps: z.array(z.object({
            app_id: z.string(),
            slug: z.string(),
            name: z.string(),
        })),
    }),
});
const SharedEnvironmentSummarySchema = z.object({
    id: z.string(),
    namespace: z.string(),
    domain: z.string().nullable(),
    deployment_url: z.string().nullable(),
    /** Commit SHA tag of the image currently running in this environment; null before first deploy. */
    latest_image_tag: z.string().nullable().optional(),
    status: z.string(),
});
const AppResourceHandleSchema = z.object({
    kind: z.string(),
    name: z.string(),
    status: z.string(),
    env_vars: z.array(z.string()),
});
const AppWorkloadHandleSchema = z.object({
    name: z.string(),
    kind: z.string(),
    spec: z.record(z.unknown()),
    status: z.string(),
    last_deployed_at: z.string().nullable(),
    // Cron-workload scheduling, surfaced as typed fields rather than inside the
    // untyped `spec` blob (docs/34 § Database Shape carries `schedule`). Null for
    // workloads that don't run on a schedule (e.g. `web`). `cron_schedule` is the
    // raw 5-field expression; the UI renders a human-readable form alongside it.
    cron_schedule: z.string().nullable(),
    last_run_at: isoDateTime().nullable(),
    next_run_at: isoDateTime().nullable(),
});
const AppGrantHandleSchema = z.object({
    integration: z.string(),
    credential: z.string(), // the requested credential slug (integration_credentials.slug)
    status: z.string(),
    declared_in_manifest: z.boolean(),
    last_status_change_at: z.string().optional(),
});
const AppUserEnvEntrySchema = z.object({
    name: z.string(),
    sensitive: z.boolean(),
    present_in_vault: z.boolean(),
});
const AppManagedEnvEntrySchema = z.object({
    name: z.string(),
    source: z.string(),
    value_known_to_agent: z.literal(false),
});
const DeletionStateSchema = z
    .object({
    /** When the delete was requested (the queued_for_deletion flip). */
    queued_at: z.string().optional(),
    /** Teardown steps that failed on the most recent incomplete pass. */
    failing_steps: z.array(z.string()).optional(),
    last_failure_at: z.string().optional(),
})
    .nullable()
    .optional();
const AppDetailSchema = z.object({
    app_id: z.string().uuid(),
    slug: z.string(),
    /** Editable public address under the apps domain; null = serves at the slug (ADR-0050). */
    serving_subdomain: z.string().nullable().optional(),
    name: z.string(),
    type: z.string(),
    status: z.string(),
    description: z.string().nullable(),
    repo_full_name: z.string().nullable(),
    icon_url: z.string().nullable().optional(),
    screenshot_urls: z.array(z.string()).optional(),
    created_at: z.string(),
    updated_at: z.string(),
    owner_email: z.string().nullable().optional(),
    /** Owner's display name (users.display_name); null when unset or the owner row is missing. */
    owner_name: z.string().nullable().optional(),
    /** Org-wide category tags declared in `greenlight.yml`, projected on deploy (ADR-0032). */
    tags: z.array(z.string()).optional(),
    doc_summary: z.string().nullable().optional(),
    doc_purpose: z.string().nullable().optional(),
    doc_architecture: z.string().nullable().optional(),
    doc_updated_at: z.string().nullable().optional(),
    /** True when the app admits only its named people and the members of its bound groups, rather than the whole org (ADR-0021 as amended by ADR-0064). */
    access_restricted: z.boolean(),
    /** Two-phase delete progress; present while `queued_for_deletion` (docs/08 §Delete flow). */
    deletion_state: DeletionStateSchema,
});
const PipelineSummarySchema = z.object({
    latest_status: z.string().nullable(),
    latest_commit_sha: z.string().nullable(),
    deployment_url: z.string().nullable(),
});
// Single-app read state: the app row plus its shared-environment summary,
// manifest projection, env contract, and pipeline info. Per ADR-0014,
// projections reflect the last-merge SHA. Mirrors the `getApp` MCP tool output.
export const GetAppResultSchema = z.object({
    app: AppDetailSchema,
    shared_environment: SharedEnvironmentSummarySchema,
    manifest_sha: z.string().nullable(),
    resources: z.array(AppResourceHandleSchema),
    workloads: z.array(AppWorkloadHandleSchema),
    grants: z.array(AppGrantHandleSchema),
    env: z.object({
        user: z.array(AppUserEnvEntrySchema),
        managed: z.array(AppManagedEnvEntrySchema),
    }),
    pipeline: PipelineSummarySchema,
    /**
     * What the app-detail banner needs to warn the owner their app is scheduled to
     * be paused. Absent when the org has the unused-app lifecycle switched off —
     * there is then nothing to warn about, and the banner never renders.
     */
    inactivity: AppInactivityNoticeSchema.optional(),
});
// ── Fleet graph (master system map) ──────────────────────────────────────────
// Compact fleet-wide topology for the /apps "Map" view (docs/28 §/apps). Unlike
// GetAppResult, this carries every app at once in a denormalized shape built
// straight from the persisted projection tables (workloads, app_resources,
// credential_grants) — no per-app manifest read. The integration hubs the map
// deduplicates and the access lanes it groups by are derived client-side from
// `apps[]`, so the payload stays minimal.
const FleetGraphWorkloadSchema = z.object({
    name: z.string(),
    kind: z.string(),
    status: z.string(),
});
const FleetGraphResourceSchema = z.object({
    // 'postgres' | 'blob' (blob_storage mapped to 'blob', matching getApp).
    kind: z.string(),
    status: z.string(),
});
// One app→integration edge. Collapsed per (app, integration) server-side; the
// status tones the edge. `credential` is the requested slug, kept for hover.
// `display_name` + `catalog_key` are integration-level (repeated per grant): the
// map's hub resolves its logo from `catalog_key → bundled brand asset` and labels
// it with `display_name`, mirroring the integration tile. `delivery_mode` is
// carried (docs/09) but not drawn at MVP.
const FleetGraphGrantSchema = z.object({
    integration: z.string(),
    display_name: z.string(),
    catalog_key: z.string().nullable(),
    credential: z.string(),
    status: z.string(),
    delivery_mode: z.enum(['proxied', 'injected']),
});
const FleetGraphAppSchema = z.object({
    app_id: z.string().uuid(),
    slug: z.string(),
    /** Editable public address under the apps domain; null = serves at the slug (ADR-0050). */
    serving_subdomain: z.string().nullable().optional(),
    name: z.string(),
    status: z.string(),
    owner_email: z.string().nullable(),
    // Signed read URL for the app icon (null → monogram fallback), and the most
    // recent successful-deploy timestamp (null → never deployed) — both mirror the
    // list read so the map's app cards can show the icon + "last deployed".
    icon_url: z.string().nullable(),
    last_deploy_at: z.string().nullable(),
    /** Latest pipeline run's status (any outcome), as in the list read; null → no runs. */
    last_pipeline_status: z.string().nullable().optional(),
    workloads: z.array(FleetGraphWorkloadSchema),
    resources: z.array(FleetGraphResourceSchema),
    /** Sorts the app into the map's "Everyone" or "Restricted" lane (ADR-0021). */
    access_restricted: z.boolean(),
    grants: z.array(FleetGraphGrantSchema),
});
// Fleet topology for the master system map. `total_apps` is the org's live-app
// count; `truncated` is true when more apps exist than `apps[]` carries (the
// read is capped — surface the cap, never silently drop apps).
export const AppFleetGraphResultSchema = z.object({
    apps: z.array(FleetGraphAppSchema),
    total_apps: z.number().int().nonnegative(),
    truncated: z.boolean(),
});
export const CreateAppSchema = z.object({
    slug: AppSlugSchema,
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    type: AppTypeSchema.default('server'),
});
/**
 * Caller-driveable lifecycle transitions on `apps.update`. Only suspend/restore
 * are user-initiated: `suspended` engages the kill switch (scale-to-zero + data
 * key revocation, docs/08 §Kill switch) and `active` restores it. Every other
 * `app_status` value is set by the platform (registration, deploy, reconciler
 * health), so the write path intentionally does not accept them.
 */
export const AppStatusTransitionSchema = z.enum(['active', 'suspended']);
/**
 * Body of `PATCH /api/apps/:id`. Carries the renameable subset of the create
 * body plus the kill-switch status transition and an optional free-text reason
 * recorded on the audit entry (the dashboard sends a fixed attribution; docs/28
 * §Lifecycle). `slug` is accepted by the schema but rejected at the service —
 * the slug is immutable after registration.
 */
export const UpdateAppSchema = CreateAppSchema.partial().extend({
    status: AppStatusTransitionSchema.optional(),
    reason: z.string().max(500).optional(),
    /**
     * Limit the app to the people named on it (ADR-0021 as amended) — owner,
     * editor, or org_admin only. False leaves it open to the org.
     */
    access_restricted: z.boolean().optional(),
    /**
     * New serving subdomain under the apps domain (ADR-0050). Same grammar as the
     * registration slug. The old URL is released immediately — no redirect.
     */
    serving_subdomain: AppSlugSchema.optional(),
});
export const UserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    role: OrgRoleSchema,
    isActive: z.boolean(),
    createdAt: isoDateTime(),
    updatedAt: isoDateTime(),
});
export const PipelineRunSchema = z.object({
    id: z.string().uuid(),
    appId: z.string().uuid(),
    status: PipelineRunStatusSchema,
    commitSha: z.string().regex(/^[0-9a-f]{7,64}$/),
    branch: z.string().min(1),
    isDefaultBranch: z.boolean(),
    pullRequestNumber: z.number().int().nullable(),
    deploymentUrl: z.string().nullable(),
    releaseNotes: z.string().nullable(),
    triggeredAt: isoDateTime(),
    completedAt: isoDateTime().nullable(),
});
export const IntegrationSchema = z.strictObject({
    id: z.string().uuid(),
    name: z.string().min(1).max(63),
    displayName: z.string().min(1).max(255),
    // Connected databases have no HTTP base URL — the proxy reaches them through
    // the SQL-execution branch (ADR-0030).
    baseUrl: z.string().url().nullable(),
    authMode: IntegrationAuthModeSchema,
    // The catalog entry this was registered from (ADR-0026); null = Custom.
    catalogKey: z.string().max(63).nullable(),
    status: IntegrationStatusSchema,
});
// Where a `service_account` static credential is placed on the wire (docs/15
// § integrations, docs/09 § Tier 1). A discriminated union on `kind`; the value
// fields name which credential field supplies each part (`secret` is the single
// vaulted secret). Stored under `auth_config.auth_inject` and never repeats
// `auth_mode`. Prefixed-scheme tokens (`Authorization: PB-Token <key>`) are a
// post-MVP `value_template` extension the MVP slice needs none of (ADR-0026 §4).
const HTTP_HEADER_NAME_RE = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;
function isSafeQueryParamName(value) {
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0;
        if (codePoint <= 31 || codePoint === 127 || '=&?#'.includes(character))
            return false;
    }
    return true;
}
export const AuthInjectSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('bearer') }),
    z.object({ kind: z.literal('basic'), username_from: z.string().min(1).default('secret') }),
    z.object({
        kind: z.literal('custom_header'),
        header_name: z.string().min(1).max(255).regex(HTTP_HEADER_NAME_RE),
    }),
    z.object({
        kind: z.literal('query_param'),
        param_name: z.string().min(1).max(255).refine(isSafeQueryParamName),
    }),
    z.object({
        kind: z.literal('multi_header'),
        headers: z
            .array(z.object({
            header_name: z.string().min(1).max(255).regex(HTTP_HEADER_NAME_RE),
            value_from: z.string().min(1),
        }))
            .min(1),
    }),
]);
export const TokenEndpointAuthMethodSchema = z.enum([
    'client_secret_post',
    'client_secret_basic',
    'private_key_jwt',
    // GitHub App installation-token mint: not an RFC 6749 grant — the proxy signs
    // an App JWT and POSTs the installation access_tokens URL (docs/09 §Tier 2).
    'github_app',
]);
// The non-secret, mode-specific params on `integrations.auth_config` (docs/15
// § integrations). Params-only: it never carries the `auth_mode` discriminant
// (that is the column) and never carries secrets (those are vaulted). By mode:
// `service_account` → `auth_inject`; `client_credentials` → `token_url`/`scopes`/
// optional `token_endpoint_auth_method`;
// `user_delegated` → `mechanism` (how the proxy obtains the signed-in user's
// upstream token, docs/48 §Building blocks) plus that mechanism's own params; the
// upstream client each credential presents lives on the credential, never here;
// `sql_password` → `username`; `service_principal` → `client_id` + `tenant_id`
// (`workload_identity` carries nothing — the identity comes from the install
// environment, ADR-0030).
export const AuthConfigSchema = z
    .object({
    auth_inject: AuthInjectSchema.optional(),
    token_url: z.string().url().optional(),
    token_endpoint_auth_method: TokenEndpointAuthMethodSchema.optional(),
    scopes: z.array(z.string()).optional(),
    mechanism: z.enum(USER_DELEGATED_MECHANISMS).optional(),
    // authorization_code (user passthrough, docs/48 §Per-user OAuth): the upstream
    // authorization server's authorize and revocation endpoints; the token
    // endpoint and the scopes the connect leg asks for are `token_url` and
    // `scopes` above.
    authorize_url: z.string().url().optional(),
    revoke_url: z.string().url().optional(),
    username: z.string().min(1).max(255).optional(),
    // client_credentials acting as the signed-in user (Box, docs/48 §Box): the
    // enterprise the user lookup runs in.
    enterprise_id: z
        .string()
        .regex(/^\d{1,20}$/)
        .optional(),
    // service_principal (connected DB): the Entra app's client (application) ID and
    // its home tenant — which is the target database's tenant. Non-secret; the client
    // secret is vaulted as the credential. tenant_id is required because the Node
    // driver does not auto-discover the server's tenant the way .NET SqlClient does.
    client_id: z.string().uuid().optional(),
    tenant_id: z.string().uuid().optional(),
})
    .passthrough()
    .superRefine((value, ctx) => {
    if ('auth_mode' in value) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'auth_config must not repeat the auth_mode discriminant',
        });
    }
    const inject = value.auth_inject;
    if (inject?.kind === 'basic' && inject.username_from !== 'secret') {
        const username = value[inject.username_from];
        if (typeof username !== 'string' || username.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [inject.username_from],
                message: `auth_config.${inject.username_from} is required for Basic authentication`,
            });
        }
    }
    if (inject?.kind === 'multi_header') {
        for (const header of inject.headers) {
            if (header.value_from === 'secret')
                continue;
            const configuredValue = value[header.value_from];
            if (typeof configuredValue !== 'string' || configuredValue.trim().length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [header.value_from],
                    message: `auth_config.${header.value_from} is required for multi-header authentication`,
                });
            }
        }
    }
});
// ── Connected-database /query gateway (docs/09 §Data Proxy, docs/16, ADR-0030) ──
// The proxy runs the statement server-side, binds supplied params, and returns
// one JSON result grid. Caps and resource budgets are canonical in docs/22.
export const CONNECTED_DB_QUERY_CAPS = {
    maxRequestBytes: 1_048_576,
    maxSqlBytes: 100_000,
    maxParams: 100,
    maxRows: 1_000,
    maxResultBytes: 1_000_000,
    maxConcurrentPerTarget: 4,
    maxPendingPerTarget: 8,
    queueWaitMs: 2_000,
    maxColumnsPerResult: 256,
    timeoutMs: 30_000,
};
// ── Storage proxy object routes (docs/09 §App object storage, docs/16, ADR-0059) ──
// Streaming PUT/GET plus HEAD/DELETE/LIST under a storage-dedicated concurrency
// pool. Caps are canonical in docs/22; the values live here so the routes, the
// docs table, and tests share one source.
export const STORAGE_OBJECT_CAPS = {
    /** Per-object size limit (100 MiB). Oversized PUTs are 413 before bytes commit. */
    maxObjectBytes: 104_857_600,
    /** Relative-key length limit in UTF-8 bytes after percent-decoding. */
    maxKeyBytes: 1024,
    /** PUT/GET wall-clock timeout (streaming transfers). */
    transferTimeoutMs: 60_000,
    /** HEAD/DELETE/LIST wall-clock timeout (metadata operations). */
    metadataTimeoutMs: 10_000,
    /** Storage-pool concurrency: active operations + queued, and the queue wait. */
    maxConcurrent: 8,
    maxPending: 32,
    queueWaitMs: 2_000,
    /** LIST page size bounds (docs/16 cursor pagination). */
    listDefaultLimit: 100,
    listMaxLimit: 1_000,
};
// Positional parameter values, bound to dialect-native placeholders ($1…/@p1…) —
// never string-concatenated. Primitives only at MVP; richer types (arrays, byte
// blobs) ride as strings the statement casts.
const ConnectedDbQueryNumberParamSchema = z
    .number()
    .refine((value) => !Number.isInteger(value) || Number.isSafeInteger(value), 'Integer parameters must be within JavaScript safe-integer range; pass larger or exact values as strings and CAST them in SQL.');
export const ConnectedDbQueryParamSchema = z.union([
    z.string(),
    ConnectedDbQueryNumberParamSchema,
    z.boolean(),
    z.null(),
]);
export const ConnectedDbQueryRequestSchema = z.object({
    sql: z
        .string()
        .min(1)
        .refine((value) => new TextEncoder().encode(value).byteLength <= CONNECTED_DB_QUERY_CAPS.maxSqlBytes, `SQL text must be at most ${CONNECTED_DB_QUERY_CAPS.maxSqlBytes} UTF-8 bytes.`),
    params: z.array(ConnectedDbQueryParamSchema).max(CONNECTED_DB_QUERY_CAPS.maxParams).optional(),
});
// Rows are arrays of values aligned with `columns` (the Snowflake statement-API
// shape) — object rows would collapse duplicate or unnamed result columns.
// `type` is the dialect's native type name, informative not normative.
export const ConnectedDbResultSetSchema = z.object({
    columns: z.array(z.object({ name: z.string(), type: z.string() })),
    rows: z.array(z.array(z.unknown())),
    row_count: z.number().int().nonnegative(),
    truncated: z.boolean(),
});
export const ConnectedDbQueryResponseSchema = ConnectedDbResultSetSchema;
export const ConnectedDbQueryErrorDetailsSchema = z.object({
    fault_origin: z.enum(['request', 'policy', 'connected_database', 'platform', 'unknown']),
    retryable: z.boolean(),
    safe_to_retry: z.boolean(),
    execution_outcome: z.enum(['not_started', 'unknown']),
    database_code: z.string().optional(),
});
// ── User directory branch (docs/40, ADR-0036) ───────────────────────────────
// The roster item the proxy's directory branch serves to apps. Strict (closed):
// platform-internal user fields — role, idp_subject, idp_groups, timestamps —
// are structurally absent, so a future users-table column can never leak
// through the directory. The branch validates every response against these
// schemas before sending.
export const DirectoryUserSchema = z
    .object({
    id: z.string().uuid(),
    email: z.string(),
    display_name: z.string().nullable(),
    is_active: z.boolean(),
})
    .strict();
export const DirectoryUsersPageSchema = z
    .object({
    items: z.array(DirectoryUserSchema),
    next_cursor: z.string().nullable(),
})
    .strict();
// Credential-backed taxonomy for integrations — see docs/09-data-brokering.md.
export const AuthCategorySchema = z.enum([
    'service-identity',
    'user-delegated',
    'connected-database',
    'provisioned-resource',
]);
// How a granted credential reaches the app (docs/09 § Delivery modes). `proxied`:
// the app calls GREENLIGHT_PROXY_URL and the proxy swaps the credential. `injected`:
// the reconciler injects the bound credential under the integration's env_var_name
// and the app calls upstream directly (docs/29 § Reserved Names).
export const IntegrationDeliveryModeSchema = z.enum(['proxied', 'injected']);
// One registered group a credential requires (ADR-0029 amendment 2026-09-17),
// named by the registry id of either source and never by a raw IdP identifier.
export const CredentialRequiredGroupSchema = z.discriminatedUnion('source', [
    z.object({ source: z.literal('greenlight'), group_id: z.string().uuid(), name: z.string() }),
    z.object({
        source: z.literal('idp'),
        idp_group_registration_id: z.string().uuid(),
        name: z.string(),
    }),
]);
// A reference to a registered group, as a write names it.
export const CredentialRequiredGroupRefSchema = z.union([
    z.strictObject({ group_id: z.string().uuid() }),
    z.strictObject({ idp_group_registration_id: z.string().uuid() }),
]);
// The non-secret parameters of a `user_delegated` credential (docs/15
// §integration_credentials, docs/48 §Catalog profiles and credentials): the
// upstream OAuth client the user connects through — on Entra an integration app
// registration, on Snowflake a security integration — and, for Snowflake, the
// role the proxy pins. Strict, so a scope list can never ride here: on Entra the
// registration is the scope set and a per-credential list would be no boundary
// (docs/48 §Why one app registration per scope set). The client's secret is the
// credential's vaulted secret, never a field here.
export const CredentialAuthConfigSchema = z.strictObject({
    client_id: z.string().min(1).max(255),
    role: z.string().min(1).max(255).optional(),
});
// One credential on an integration: a stable slug (the handle a grant binds on),
// a free-text scope describing what the upstream key grants, a Key Vault path,
// whether the secret has been written, and how many apps hold a grant on this
// specific credential. Post-MVP integrations have configured=false until they ship.
// What a credential test concluded (docs/09 §Testing a credential).
export const CREDENTIAL_TEST_OUTCOMES = [
    'connected',
    'unreachable',
    'credential_failed',
    'auth_rejected',
    'not_permitted',
    'not_found',
    'unexpected_response',
    'not_supported',
];
export const CredentialTestOutcomeSchema = z.enum(CREDENTIAL_TEST_OUTCOMES);
export const IntegrationCredentialSchema = z.object({
    slug: z.string(), // stable handle, unique per integration; referenced by grants[].credential
    scope: z.string(), // free-text capability description (IT-authored); descriptive only
    // Null for a secretless credential (workload_identity connected DB — ADR-0030).
    vault_key: z.string().nullable(),
    configured: z.boolean(),
    apps_count: z.number().int().nonnegative(), // apps holding a grant on this credential
    approval_mode: IntegrationApprovalModeSchema,
    // The registered groups whoever stands behind a grant must be in; empty = no requirement.
    required_groups: z.array(CredentialRequiredGroupSchema),
    // The upstream OAuth client a `user_delegated` credential presents (and a
    // Snowflake role); null on every other mode. Read as an open record like the
    // integration's `auth_config`, so a stored row always renders; writes validate.
    auth_config: z.record(z.unknown()).nullable(),
    // The last credential test IT ran on this saved credential; null until one runs.
    last_test: z
        .object({
        outcome: CredentialTestOutcomeSchema,
        tested_at: z.string().datetime({ offset: true }),
        // The email of the person who ran it; null for a test from before it was recorded.
        tested_by: z.string().nullable().optional(),
    })
        .nullable()
        .optional(),
});
// One per-user OAuth user connection as the lifecycle surfaces read it (docs/48
// §Per-user OAuth, docs/15 §user_connections): who, through which credential, in
// what state. Never token material — the row's blob has no read projection.
export const UserConnectionStatusSchema = z.enum(USER_CONNECTION_STATUSES);
export const UserConnectionSchema = z.object({
    id: z.string().uuid(),
    user: z.object({
        id: z.string().uuid(),
        email: z.string(),
        display_name: z.string().nullable(),
    }),
    integration: z.object({
        id: z.string().uuid(),
        slug: z.string(),
        name: z.string(),
    }),
    credential: z.object({
        id: z.string().uuid(),
        slug: z.string(),
    }),
    status: UserConnectionStatusSchema,
    // The upstream's name for the user, as the connect callback matched it.
    upstream_subject: z.string().nullable(),
    connected_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    revoked_at: z.string().datetime({ offset: true }).nullable(),
});
export const UserConnectionListSchema = z.object({ items: z.array(UserConnectionSchema) });
// Every revoke answers with the count it flipped; a disconnect that found
// nothing to flip answers zero rather than 404, so the surface never confirms
// which ids exist.
export const UserConnectionRevokeResultSchema = z.object({
    revoked: z.number().int().nonnegative(),
});
// What the Connect page shows before the person clicks: the integration with
// what its mark resolves from, the app named by `return_to` with its icon and
// owner, and whether their own connection is missing, live, or needs making
// again. Never a URL from the query string; the icon is a freshly signed read URL.
export const ConnectTargetSchema = z.object({
    integration: z.object({
        id: z.string().uuid(),
        name: z.string(),
        slug: z.string(),
        catalog_key: z.string().nullable(),
    }),
    credential: z.object({ slug: z.string() }),
    status: z.enum(['not_connected', 'active', 'needs_reauth']),
    app: z
        .object({
        name: z.string(),
        icon_url: z.string().nullable(),
        /** The owner's display name, or their email when no name is set. */
        owner_name: z.string().nullable(),
    })
        .nullable(),
});
// Denormalized snake_case projection for the list / inspect surfaces.
// Mirrors the pattern of AppListItemSchema — a read-side shape distinct from
// the DB row (IntegrationSchema). See docs/09-data-brokering.md.
// How the control plane trusts an HTTPS service_account upstream's certificate
// (docs/15 §integrations). Null on the row means verify against the system
// roots. `ca_pem` is non-secret: certificates only, never a key.
export const TLS_CA_PEM_MAX_BYTES = 64 * 1024;
export const TLS_CA_MAX_CERTIFICATES = 16;
export const IntegrationTlsConfigSchema = z.discriminatedUnion('mode', [
    z.strictObject({
        mode: z.literal('custom_ca'),
        ca_pem: z.string().min(1).max(TLS_CA_PEM_MAX_BYTES, 'ca_pem must be at most 64 KiB'),
    }),
    z.strictObject({ mode: z.literal('skip_verify') }),
]);
export const IntegrationListItemSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    kind: z.string(),
    // The catalog entry this was registered from (ADR-0026); null for a Custom
    // (catalog-less) registration. The dashboard tile resolves its logo from it.
    catalog_key: z.string().nullable(),
    auth_mode: z.string(),
    // Effective OAuth client-authentication method for a client_credentials row
    // (`client_secret_post` when unset in auth_config), null for every other
    // mode. Non-secret; the dialogs read it to shape which write-only secret
    // fields a credential collects (a private_key_jwt secret is the
    // {client_id, certificate_id, private_key} triple, not an id + secret pair).
    token_endpoint_auth_method: TokenEndpointAuthMethodSchema.nullable(),
    auth_category: AuthCategorySchema,
    // The non-secret, mode-specific params the integration was registered with
    // (docs/15 §integrations). Read as an open record rather than AuthConfigSchema:
    // a row stored before a constraint existed must still be readable, or the one
    // surface that could repair it fails to load. Writes still validate.
    auth_config: z.record(z.unknown()).nullable(),
    credentials: z.array(IntegrationCredentialSchema),
    delivery_mode: IntegrationDeliveryModeSchema,
    // The env var the bound credential is injected under; non-null only when
    // delivery_mode === 'injected' (docs/29 § Reserved Names).
    env_var_name: z.string().nullable(),
    status: IntegrationStatusSchema.exclude(['deleted']),
    built_in: z.boolean(),
    post_mvp: z.boolean(),
    apps_count: z.number().int().nonnegative(),
    last_access_at: isoDateTime().nullable(),
    registered_by: z.string(),
    registered_at: isoDateTime(),
    // null for connected databases (no HTTP base URL — docs/09 § Connected databases).
    base_url: z.string().url().nullable(),
    tls_config: IntegrationTlsConfigSchema.nullable(),
    // Connected-database target (ADR-0030); all four null for HTTP integrations.
    engine: z.string().nullable(),
    host: z.string().nullable(),
    port: z.number().int().nullable(),
    database_name: z.string().nullable(),
    execution_mode: z.string(),
    identity_note: z.string(),
    degraded_note: z.string().nullable(),
    post_mvp_note: z.string().nullable(),
});
// Dashboard twin of MCP `listGrantableIntegrations` for the citizen-dev prompt
// on `/apps` (docs/28). Slug, display name, and catalog_key only — no vault
// keys, credentials, or IT registry fields. Inactive integrations are omitted
// by the service (`availableOnly`), matching the MCP tool.
export const GrantableIntegrationSummarySchema = z.object({
    integration: z.string(),
    name: z.string(),
    catalog_key: z.string().nullable(),
});
// The updated row's editable-surface projection, returned by PATCH
// /api/integrations/:id (docs/09 § Integration Editing). Snake_case like
// IntegrationListItemSchema, which this echoes a subset of — auth_mode is
// included read-only for context (it is not itself editable: docs/09).
export const IntegrationEditSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    base_url: z.string().url().nullable(),
    tls_config: IntegrationTlsConfigSchema.nullable(),
    auth_mode: IntegrationAuthModeSchema,
    // The non-secret, mode-specific params the integration was registered with
    // (docs/15 §integrations). Read as an open record rather than AuthConfigSchema:
    // a row stored before a constraint existed must still be readable, or the one
    // surface that could repair it fails to load. Writes still validate.
    auth_config: z.record(z.unknown()).nullable(),
    delivery_mode: IntegrationDeliveryModeSchema,
    env_var_name: z.string().nullable(),
    engine: z.string().nullable(),
    host: z.string().nullable(),
    port: z.number().int().nullable(),
    database_name: z.string().nullable(),
    status: IntegrationStatusSchema,
    updated_at: isoDateTime(),
});
// An app whose grant would be affected by a delivery-mode or env-var-name edit —
// same shape as the blocking-apps list on the delete-guard 409 (docs/09 §
// Integration Deletion), reused here as an informational (never-blocking) warning.
export const AffectedAppSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
});
export const UpdateIntegrationResponseSchema = z.object({
    integration: IntegrationEditSchema,
    // Apps holding a live-or-in-flight grant against this integration, populated
    // only when the edit changed delivery_mode or env_var_name — the fields that
    // change what a deployed pod receives on its next deploy/restart (T-1005).
    // Never blocks the edit; empty when no such field changed or no grant exists.
    affected_apps: z.array(AffectedAppSchema),
});
export const PrincipalTypeSchema = z.enum(PRINCIPAL_TYPES);
export const PermissionSchema = z.object({
    id: z.string().uuid(),
    principalType: PrincipalTypeSchema,
    principalId: z.string().uuid(),
    appId: z.string().uuid().nullable(),
    integrationId: z.string().uuid(),
    credentialSlug: z.string(),
    status: PermissionStatusSchema,
    reason: z.string().nullable(),
    requestedAt: isoDateTime(),
    reviewedAt: isoDateTime().nullable(),
    reviewedBy: z.string().uuid().nullable(),
    heldReason: z.string().nullable(),
});
// Denormalized snake_case projection for the cross-app permission review queue.
// Mirrors IntegrationListItemSchema — a read-side shape (app + integration names
// resolved) distinct from the row model (PermissionSchema). See docs/28-it-dashboard-ui.md.
export const PermissionListItemSchema = z.object({
    id: z.string().uuid(),
    principal_type: PrincipalTypeSchema,
    principal_id: z.string().uuid(),
    // App identity for app-principal rows; null for user grants (ADR-0029).
    app_id: z.string().uuid().nullable(),
    app_name: z.string().nullable(),
    app_slug: z.string().nullable(),
    integration_id: z.string().uuid(),
    integration_name: z.string(),
    integration_slug: z.string(),
    credential_slug: z.string(),
    // Free-text scope of the bound credential (descriptive, never enforced). Null
    // when the grant names a slug the integration has not registered (unservable).
    credential_scope: z.string().nullable(),
    // Approving a grant on an injected credential discloses its raw value to the
    // principal (ADR-0039); the queue row warns on it.
    delivery_mode: IntegrationDeliveryModeSchema,
    // The integration's auth mode, so the decision step can say what approving a
    // `user_delegated` grant means: the upstream sees each signed-in user and
    // background work cannot use it (docs/48 §The core).
    auth_mode: IntegrationAuthModeSchema,
    // True when the integration's catalog profile mints per signed-in user under a
    // mode that otherwise reads as a shared identity (Box, docs/48 §Box): no user
    // connects, and background work cannot use the grant.
    acts_as_signed_in_user: z.boolean(),
    // The upstream OAuth client the bound credential presents (its client id) on a
    // `user_delegated` integration; null elsewhere and when the grant is unservable.
    credential_client_id: z.string().nullable(),
    status: PermissionStatusSchema,
    requested_by: z.string(),
    requested_at: isoDateTime(),
    reviewed_by: z.string().nullable(),
    reviewed_at: isoDateTime().nullable(),
    reason: z.string().nullable(),
    auto_approved: z.boolean(),
    // Why the credential's group requirement holds the grant at pending; null when not held.
    held_reason: z.string().nullable(),
});
// A group membership as the /users projection reports it. `source` says where
// the membership lives: an `idp` row is the directory's answer and read-only,
// a `greenlight` row is `group_members` and editable here (docs/41 §UI).
export const UserGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    source: GroupSourceSchema,
});
export const UserAccessibleAppSchema = z.object({
    app_id: z.string().uuid(),
    app_name: z.string(),
    app_slug: z.string(),
});
// The group an editor relationship came from, on an entry that is not an
// `app_co_owners` row (docs/41 §Role). Null on a direct row, which is the only
// kind the co-owner remove op can delete.
export const CoOwnerGroupOriginSchema = z.object({
    id: z.string(),
    name: z.string(),
    source: GroupSourceSchema,
});
export const UserOwnedAppSchema = z.object({
    app_id: z.string().uuid(),
    app_name: z.string(),
    app_slug: z.string(),
    is_co_owner: z.boolean(),
    via_group: CoOwnerGroupOriginSchema.nullable(),
});
// Denormalized snake_case projection for the /users list + inspector.
// Read-side shape distinct from the write-side UserSchema (mirrors AppListItemSchema).
export const UserListItemSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    display_name: z.string().nullable(),
    role: OrgRoleSchema,
    is_active: z.boolean(),
    idp_subject: z.string().nullable(),
    groups: z.array(UserGroupSchema).optional(),
    accessible_apps: z.array(UserAccessibleAppSchema).optional(),
    owned_apps: z.array(UserOwnedAppSchema).optional(),
    created_at: isoDateTime(),
});
// Greenlight-managed groups (docs/41 §Admin API). `member_count` is the group's
// own membership; `app_count` is the bindings that keep it from being deleted.
export const GroupListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    member_count: z.number().int().nonnegative(),
    app_count: z.number().int().nonnegative(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
});
// A group's membership page — the user projection, not the full user record.
export const GroupMemberSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    display_name: z.string().nullable(),
    role: OrgRoleSchema,
    is_active: z.boolean(),
    added_at: isoDateTime(),
});
// A directory group IT has registered so app owners can pick it by label
// (ADR-0064 amendment C). `known_member_count` counts users whose last login
// carried the identifier — what Greenlight knows, not what the directory holds.
export const IdpGroupRegistrationSchema = z.object({
    id: z.string().uuid(),
    idp_group_id: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    app_count: z.number().int().nonnegative(),
    known_member_count: z.number().int().nonnegative(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
});
// A group bound to an app (docs/41 §App access through groups). One row binds
// exactly one source: a Greenlight group by `group_id`, or a registered
// directory group by `idp_group_registration_id` — an owner never handles a raw
// IdP identifier. `id` is the binding row; `member_count` is what the binding
// admits, which for an IdP group is only the members Greenlight has seen sign in.
export const AppGroupBindingSourceSchema = z.enum(['greenlight', 'idp']);
export const AppGroupBindingSchema = z.object({
    id: z.string().uuid(),
    app_id: z.string().uuid(),
    source: AppGroupBindingSourceSchema,
    group_id: z.string().uuid().nullable(),
    idp_group_registration_id: z.string().uuid().nullable(),
    name: z.string(),
    role: AppAccessRoleSchema,
    member_count: z.number().int().nonnegative(),
    created_at: isoDateTime(),
});
// One result from the Access tab's single search control (docs/41 §UI): people,
// Greenlight groups, and registered directory groups resolved into one labelled
// list. A registered group carries IT's label as `name`, exactly as a binding
// does, so an owner never meets a directory identifier. `member_count` on an
// `idp_group` is what Greenlight has seen sign in, not the directory's answer.
export const AppAccessCandidateSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('person'),
        user_id: z.string().uuid(),
        email: z.string().email(),
        display_name: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('group'),
        group_id: z.string().uuid(),
        name: z.string(),
        description: z.string().nullable(),
        member_count: z.number().int().nonnegative(),
    }),
    z.object({
        kind: z.literal('idp_group'),
        idp_group_registration_id: z.string().uuid(),
        name: z.string(),
        description: z.string().nullable(),
        member_count: z.number().int().nonnegative(),
    }),
]);
// Who a group bound to an app admits, read from Greenlight's own tables and
// scoped to an owner's binding decision (ADR-0064 amendment D). `coverage`
// names which answer this is: `complete` for a Greenlight group's
// `group_members`, `signed_in` for a directory group, where Greenlight knows
// only the members whose last login carried the identifier.
export const AppGroupMemberSchema = z.object({
    user_id: z.string().uuid(),
    email: z.string().email(),
    display_name: z.string().nullable(),
    is_active: z.boolean(),
});
export const AppGroupMembersSchema = z.object({
    items: z.array(AppGroupMemberSchema),
    next_cursor: z.string().nullable(),
    coverage: z.enum(['complete', 'signed_in']),
});
// Tail-capped raw log payload for a check, proxied live from the SCM runner
// (build/scan) or snapshotted from the deploy's pod — the startup tail on a green
// rollout, the crash snapshot on failure. Defined once here so the
// provider seam (`ScmProvider.getCiRunLogs`) and the per-check `logs?` field share one
// named type — see 07-source-control-pipeline.md and 37-alternative-scm-providers.md.
// Not cursor-paginated: pagination belongs to the runtime `getLogs` tool.
export const CiRunLogsSchema = z.object({
    text: z.string(),
    truncated: z.boolean(),
});
// Per-check result row inside a pipeline run; mirrors `pipeline_run_checks`
// per 15-database-schema.md §pipeline_run_checks. `details` surfaces the producer-shaped
// `error_details` jsonb array verbatim; `logs` is present only on a `detail='full'` read
// for checks that have raw output (06-agent-interface.md §getPipelineRun).
export const PipelineCheckResultSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.enum(['running', 'pass', 'fail', 'skip']),
    duration_ms: z.number().int().nullable(),
    error_summary: z.string().nullable(),
    suggested_fix: z.string().nullable(),
    details: z.array(z.record(z.unknown())),
    // The structured rollout-failure classification lifted out of `details` so an agent
    // reads the code/owner/next-step without parsing the open detail array or calling
    // getAppDiagnostics (08-app-deployment.md#deploy-state-machine). Null on non-deploy
    // checks and on deploys that failed without a classified blocker.
    blocker: RolloutBlockerSchema.nullable(),
    logs: CiRunLogsSchema.optional(),
    started_at: z.string(),
    completed_at: z.string().nullable(),
});
export const OpaDecisionSchema = z.object({
    allow: z.boolean(),
    policy_id: z.string(),
    evaluated_at: z.string(),
    reasons: z.array(z.string()),
    // Raw rego/JSON payload, surfaced verbatim by the run detail viewer.
    payload: z.record(z.unknown()),
});
// Where the merge→live wall-clock went. Stamps are ISO-8601; segments are integer
// milliseconds, null where a stage did not run or the run predates the workflow
// render that stamps them (16-api-conventions.md §Timestamps). There is no
// `deploy_ms` here: that is the `deploy` check's own `duration_ms`.
export const PipelineStageTimingsSchema = z.object({
    triggered_at: isoDateTime(),
    runner_started_at: isoDateTime().nullable(),
    build_started_at: isoDateTime().nullable(),
    build_completed_at: isoDateTime().nullable(),
    push_completed_at: isoDateTime().nullable(),
    completed_at: isoDateTime().nullable(),
    queue_ms: z.number().int().nullable(),
    build_ms: z.number().int().nullable(),
    push_ms: z.number().int().nullable(),
    total_ms: z.number().int().nullable(),
});
export const PipelineRunDetailSchema = z.object({
    run: PipelineRunSchema,
    checks: z.array(PipelineCheckResultSchema),
    opa_decision: OpaDecisionSchema.nullable(),
    stage_timings: PipelineStageTimingsSchema,
    // Set only when an MCP `getPipelineRun(wait=true)` long-poll times out with the run
    // still non-terminal (06-agent-interface.md §getPipelineRun). REST never sets it.
    retry_after_seconds: z.number().int().optional(),
});
// Env-var contract (docs/29 § MCP Tools). The same shapes flow over MCP
// (env*) and REST (/api/apps/:id/env); the schema is the single
// source of truth for both. Values live only in the vault — payloads carry a
// value only on explicit, non-sensitive reveal.
// `shared` is the only environment scope at MVP; an omitted scope resolves to it.
export const EnvScopeSchema = z.literal('shared');
// A user-controlled env var: name declared in greenlight.yml `env:`, value in vault.
export const EnvListUserEntrySchema = z.object({
    name: z.string(),
    env: EnvScopeSchema,
    sensitive: z.boolean(),
    // Non-null only when reveal=true, the var is non-sensitive, and a value exists.
    value: z.string().nullable(),
    vault_secret_name: z.string(),
    present_in_vault: z.boolean(),
    present_in_manifest: z.boolean(),
    created_by_email: z.string().nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});
// A managed (derived) env var per the docs/29 derivation table. Never carries a value.
export const EnvListManagedEntrySchema = z.object({
    name: z.string(),
    source: z.string(),
    value_known_to_agent: z.literal(false),
});
// `envList` response: declared user names + derived managed names.
export const EnvContractResponseSchema = z.object({
    user: z.array(EnvListUserEntrySchema),
    managed: z.array(EnvListManagedEntrySchema),
});
// One step in the reconcile-style envelope envSet / envRemove return.
export const EnvExecutedOpSchema = z.object({
    op: z.string(),
    status: z.enum(['ok', 'failed']),
    detail: z.string().optional(),
});
// `envSet` response. At MVP this never reconciles or restarts the pod
// (ADR-0014); `note` restates that the pod picks up the value on the next merge.
export const EnvSetResponseSchema = z.object({
    plan: z.string(),
    executed_ops: z.array(EnvExecutedOpSchema),
    status: z.enum(['complete', 'failed', 'running']),
    app_id: z.string(),
    env: EnvScopeSchema,
    name: z.string(),
    sensitive: z.boolean(),
    vault_secret_name: z.string(),
    change: z.enum(['created', 'updated']),
    present_in_manifest: z.boolean(),
    note: z.string(),
});
// `envRemove` response. Same envelope; `removed` is false (not an error)
// when no value was set.
export const EnvRemoveResponseSchema = z.object({
    plan: z.string(),
    executed_ops: z.array(EnvExecutedOpSchema),
    status: z.enum(['complete', 'failed', 'running']),
    app_id: z.string(),
    env: EnvScopeSchema,
    name: z.string(),
    removed: z.boolean(),
    note: z.string(),
});
// Env-var name: uppercase, digits, underscores (docs/29 § Reserved Names). The
// reserved-name set is rejected by the service, not here.
export const EnvVarNameSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{0,127}$/, { message: 'env name must match ^[A-Z][A-Z0-9_]{0,127}$' });
// `env` accepts any string at the boundary; a non-`shared` scope is rejected by
// the service with env.invalid_scope (parity with the MCP tools), not at parse time.
export const SetEnvVarBodySchema = z.object({
    name: EnvVarNameSchema,
    // Non-empty: an empty env value is almost always a mistake, and the prior
    // contract enforced this. To intentionally clear a value, use envRemove.
    value: z.string().min(1, { message: 'value must not be empty' }),
    sensitive: z.boolean(),
    reason: z.string().min(1),
    env: z.string().optional(),
});
export const RemoveEnvVarBodySchema = z.object({
    reason: z.string().min(1),
    env: z.string().optional(),
});
export const EnvListQuerySchema = z.object({
    // A query string is always text, and `z.coerce.boolean()` follows JS
    // truthiness — every non-empty string (including "false"/"0") becomes true,
    // so `reveal=false` would wrongly disclose plain values. Accept only the
    // explicit literals and map them; anything else fails query validation
    // (docs/29 AC-ENV-03).
    reveal: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    env: z.string().optional(),
    name: z.string().optional(),
});
export const AppCoOwnerSchema = z.object({
    user_id: z.string().uuid(),
    email: z.string(),
    display_name: z.string().nullable(),
    added_at: z.string(),
    // The two sources are independent, so both can be set: `direct` says the
    // remove op has a row to delete, `via_group` says a binding grants the role
    // whatever that op does.
    direct: z.boolean(),
    via_group: CoOwnerGroupOriginSchema.nullable(),
});
// ── Pod logs (read live from the kubelet) — see 11-observability.md#logs and
// 06-agent-interface.md getLogs. The same row + page shapes flow across the
// REST tail endpoint and the MCP getLogs tool.
// One log line. stdout and stderr come back merged (the kubelet read can't
// separate them); `timestamp` is the line's real emission time.
export const LogRowSchema = z.object({
    timestamp: isoDateTime(),
    message: z.string(),
    pod: z.string().optional(),
    container: z.string().optional(),
});
// Crash-loop context attached to a log page when the app's pods are restarting.
export const LogCrashContextSchema = z.object({
    crash_looping: z.boolean(),
    restart_count: z.number().int().nonnegative(),
    reason: z.string().optional(),
    last_terminated_at: isoDateTime().optional(),
});
// A bounded, cursor-paginated window of log rows. Extends the canonical
// `{ items, next_cursor }` page with the queried window bounds, a truncation
// flag, and optional crash context.
export const LogsPageSchema = z.object({
    items: z.array(LogRowSchema),
    next_cursor: z.string().nullable(),
    window_start: isoDateTime(),
    window_end: isoDateTime(),
    truncated: z.boolean(),
    crash_context: LogCrashContextSchema.optional(),
});
// ── Per-app resource metrics (latest 1-minute sample) — see
// 11-observability.md#per-app-metrics-persistence and 06-agent-interface.md
// getMetrics. The same shape flows across the REST metrics endpoint and the MCP
// getMetrics tool; it mirrors `GetMetricsResult` in @greenlight/mcp. Every field
// (incl. collected_at) is null until a sample has been collected, and any single
// field is null when its source was unreachable at collection time.
export const AppMetricsSchema = z.object({
    collected_at: isoDateTime().nullable(),
    cpu_usage_percent: z.number().nullable(),
    memory_usage_mb: z.number().nullable(),
    restart_count: z.number().nullable(),
    request_rate_per_min: z.number().nullable(),
    error_rate: z.number().nullable(),
    /** Millicores the app is measured using. */
    cpu_used_m: z.number().nullable(),
    /** Millicores the scheduler reserves for the app whether or not it spends them. */
    cpu_reserved_m: z.number().nullable(),
    /** MiB the scheduler reserves for the app; compare against `memory_usage_mb`. */
    memory_reserved_mb: z.number().nullable(),
});
// ── Runtime diagnostics snapshot — see 11-observability.md#pod-diagnostics and
// 06-agent-interface.md getAppDiagnostics. One shape across the REST
// `/diagnostics` endpoint and the MCP tool. Best-effort: a section is null when
// its source was unreachable, never gap-filled. Env values never appear.
export const DiagnosticsProbeSchema = z.object({
    ok: z.boolean(),
    recent_failures: z.number().int().nonnegative(),
    message: z.string().nullable(),
});
export const AppDiagnosticsSchema = z.object({
    workload: z
        .object({
        phase: z.string(),
        ready: z.boolean(),
        restart_count: z.number().int().nonnegative(),
        last_restart_reason: z.string().nullable(),
        last_terminated: z
            .object({
            exit_code: z.number().int().nullable(),
            signal: z.number().int().nullable(),
            oom_killed: z.boolean(),
            at: isoDateTime().nullable(),
        })
            .nullable(),
        started_at: isoDateTime().nullable(),
        running_image_tag: z.string().nullable(),
    })
        .nullable(),
    probes: z
        .object({
        readiness: DiagnosticsProbeSchema,
        liveness: DiagnosticsProbeSchema,
    })
        .nullable(),
    resources: z
        .object({
        collected_at: isoDateTime().nullable(),
        cpu_usage_percent: z.number().nullable(),
        memory_usage_mb: z.number().nullable(),
        memory_reserved_mb: z.number().nullable(),
        near_limit: z.boolean().nullable(),
        recent_oom: z.boolean().nullable(),
    })
        .nullable(),
    env_contract: z
        .array(z.object({
        name: z.string(),
        source: z.enum(['declared', 'managed', 'integration']),
        status: z.enum(['ok', 'missing', 'stale']),
    }))
        .nullable(),
    connectivity: z
        .array(z.object({
        target: z.string(),
        host: z.string().nullable(),
        reachable: z.boolean(),
        allowed_by_pod_egress: z.boolean(),
        latency_ms: z.number().nullable(),
    }))
        .nullable(),
    recent_events: z
        .array(z.object({
        type: z.string(),
        reason: z.string(),
        message: z.string(),
        count: z.number().int().positive(),
        at: isoDateTime().nullable(),
    }))
        .nullable(),
    freshness: z
        .object({
        running_image_tag: z.string().nullable(),
        expected_image_tag: z.string().nullable(),
        image_up_to_date: z.boolean().nullable(),
        env_secret_current: z.boolean().nullable(),
    })
        .nullable(),
    rollout_blocker: RolloutBlockerSchema.nullable(),
    crash_snapshot_ref: z.object({ run_id: z.string().uuid() }).nullable(),
});
// ── Per-app metrics history series — see 11-observability.md#per-app-metrics-persistence
// and 06-agent-interface.md getMetricsSeries. A downsampled time series over a
// requested window, the same shape across the REST `/metrics/series` endpoint and
// the MCP getMetricsSeries tool. Points are date_bin-bucketed averages over the
// `app_metrics` samples; summary stats are computed over the raw samples in range
// so an agent can reason about a trend without ingesting every point.
// The two charted resource metrics. CPU is a percentage, memory is megabytes —
// only these two are series-charted at MVP (restarts stay a current-only counter).
export const AppMetricSeriesMetricSchema = z.enum(['cpu', 'memory']);
// Bucket width. The dashboard range selector pairs 1h→1m, 24h→5m, 7d→1h; an agent
// may pass any of these. The interim source is 1-minute, so finer is meaningless.
export const AppMetricSeriesStepSchema = z.enum(['1m', '5m', '1h']);
// Query for the series read. `from`/`to` are ISO-8601 (Prometheus-style explicit
// window — no server "now" default, so the read stays deterministic and clock-free);
// they are range-validated in the service (from<to, window ≤ retention) so a bad
// value returns the canonical `validation.*` shape with request_id.
export const AppMetricsSeriesQuerySchema = z.object({
    metric: AppMetricSeriesMetricSchema,
    from: isoDateTime(),
    to: isoDateTime(),
    step: AppMetricSeriesStepSchema,
});
// One bucket: its start instant and the average of the samples that fell in it.
// `value` is null for an empty bucket (a gap in collection), never 0.
export const AppMetricsPointSchema = z.object({
    t: isoDateTime(),
    value: z.number().nullable(),
});
// Aggregate stats over the raw samples in range (not over the bucketed points, so
// p95 reflects real sample spread). Null overall when the window holds no samples.
export const AppMetricsSummarySchema = z.object({
    min: z.number(),
    max: z.number(),
    avg: z.number(),
    p95: z.number(),
});
export const AppMetricsSeriesSchema = z.object({
    metric: AppMetricSeriesMetricSchema,
    // Unit of every value/summary number, so a consumer can label the series
    // without re-deriving it from `metric`.
    unit: z.enum(['percent', 'mb']),
    from: isoDateTime(),
    to: isoDateTime(),
    step: AppMetricSeriesStepSchema,
    points: z.array(AppMetricsPointSchema),
    summary: AppMetricsSummarySchema.nullable(),
});
// Query for the REST log tail. `limit` overrides the generic 100 cap with a
// log-specific default 100 / max 1000 (runtime logs are diagnostic payloads).
// `since`/`until` accept an ISO-8601 timestamp or a relative form (`5m`, `1h`);
// they are validated in the service so a bad value returns `validation.*`.
export const LogsQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(1000).optional(),
    since: z.string().optional(),
    until: z.string().optional(),
    filter: z.string().optional(),
    // A query string is always text, and `z.coerce.boolean()` follows JS
    // truthiness — every non-empty string (including "false"/"0") becomes true,
    // so a caller could never turn previous-container logs off. Accept only the
    // explicit literals and map them; anything else fails query validation.
    previous: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
// Query for the live log stream (`GET /api/apps/:id/logs/stream`, an SSE
// response). The stream resumes after `cursor` when given, else starts at
// `since`, else at connect time. `filter` narrows like the tail's.
export const LogsStreamQuerySchema = LogsQuerySchema.pick({
    cursor: true,
    since: true,
    filter: true,
});
// `event: line` payload. `cursor` is a tail cursor positioned after this row,
// so a client that drops to polling resumes exactly where the stream stopped.
export const LogStreamLineEventSchema = z.object({
    row: LogRowSchema,
    cursor: z.string(),
});
// `event: end` payload, sent before the server closes the stream.
// `rotate`: the connection hit its lifetime; reconnect with the last cursor.
// `pods_ended`: every followed pod stream ended (termination, suspend, redeploy).
// `unavailable`: the kubelet read failed.
export const LogStreamEndReasonSchema = z.enum(['rotate', 'pods_ended', 'unavailable']);
export const LogStreamEndEventSchema = z.object({ reason: LogStreamEndReasonSchema });
// ── Plugin marketplace bootstrap — see docs/20-agent-plugins.md
// §marketplace-repo-bootstrap. The persisted marketplace-repo status, shared by
// the install-wizard step and the Settings surface. snake_case API shape; the
// server maps it from the camelCase plugin_marketplaces row.
export const PluginMarketplaceVisibilitySchema = z.enum(['public', 'private']);
export const DEFAULT_PLUGIN_MARKETPLACE_REPO_NAMES = {
    public: 'greenlight-agent-plugins',
    private: 'greenlight-agent-plugins-private',
};
// Fixed marketplace name baked into every generated repo's marketplace.json
// files (docs/20 §plugin packaging) — clients resolve `greenlight@<this name>`,
// so install snippets must key on it, never on an org-specific value.
export const PLUGIN_MARKETPLACE_NAME = 'greenlight-tools';
// GitHub silently rewrites names outside this set (spaces/slashes → dashes),
// which would break repository-scoped idempotency: the persisted full name
// would never match the requested one again. Reject those up front instead.
export const PLUGIN_MARKETPLACE_REPO_NAME_PATTERN = /^(?!\.{1,2}$)[A-Za-z0-9._-]{1,100}$/;
export const BootstrapPluginMarketplaceBodySchema = z.object({
    repo_name: z
        .string()
        .trim()
        .regex(PLUGIN_MARKETPLACE_REPO_NAME_PATTERN, 'Repository names may only contain letters, numbers, ".", "-", and "_".')
        .optional(),
    visibility: PluginMarketplaceVisibilitySchema.default('public'),
});
export const PluginMarketplaceStatusSchema = z.object({
    id: z.string().uuid(),
    repo_full_name: z.string(),
    visibility: PluginMarketplaceVisibilitySchema,
    default_branch: z.string(),
    last_synced_template_version: z.string(),
    last_synced_sha: z.string(),
    last_synced_at: z.string(),
    bootstrapped_at: z.string(),
});
export const PluginMarketplaceIdParamsSchema = z.object({ id: z.string().uuid() });
// Unregistering retires the row (Greenlight never touches the GitHub repo
// itself) — the response confirms which row and when, for the Settings table
// to drop it optimistically pending the next settings refetch.
export const PluginMarketplaceUnregisterResultSchema = z.object({
    id: z.string().uuid(),
    unregistered_at: z.string(),
});
// What a bootstrap call did: created/recreated (a repo write happened),
// up_to_date (repo present and current), or stale (present but content drifted —
// handed to the reconciler, not re-committed here).
export const MarketplaceBootstrapOutcomeSchema = z.enum([
    'created',
    'recreated',
    'up_to_date',
    'stale',
]);
export const PluginMarketplaceBootstrapResultSchema = z.object({
    outcome: MarketplaceBootstrapOutcomeSchema,
    marketplace: PluginMarketplaceStatusSchema,
});
// ── Org settings — see docs/28-it-dashboard-ui.md §/settings and
// docs/12-azure-installation.md (install-set fields). Admin-gated sections
// are `.optional()` so partial/degraded payloads still validate.
export const CURSOR_USAGE_FAILURES = ['key_rejected', 'unreachable'];
export const CursorUsageSettingsSchema = z.object({
    admin_key_configured: z.boolean(),
    /** The last pass that read Cursor's usage events without error. */
    synced_at: z.string().datetime({ offset: true }).nullable(),
    /** Set while the latest pass failed; cleared by the next one that succeeds. */
    failure: z
        .object({
        code: z.enum(CURSOR_USAGE_FAILURES),
        at: z.string().datetime({ offset: true }),
    })
        .nullable(),
});
const orgSettingsSchema = z.object({
    // The Org section is org-admin only (docs/28 §/settings), so a non-admin
    // GET /api/settings omits it. Only `agent_setup` is guaranteed present.
    org: z
        .object({
        display_name: z.string(),
        primary_domain: z.string().max(253),
        apps_subdomain: z.string().max(253),
        control_plane_url: z.string().url(),
        // Which cloud this install runs on (docs/39) — drives provider-appropriate
        // labels/copy (e.g. the DNS zone-delegation step's service name).
        cloud: z.enum(CLOUD_PROVIDERS),
        // App marketplace rollout gate (ADR-0038, docs/42) — distinct from the
        // Whether a newly registered app starts open to the org or restricted to
        // the people its owner names. PATCH /api/settings/app-discoverability.
        apps_discoverable_by_default: z.boolean(),
        // Whether platform feedback is sent to Shift automatically as it arrives.
        // Off means manual — Download, or an explicit Send to Shift on /feedback.
        // Turning it on sends the title and body of new items out of the install,
        // so it ships off and the change is audited (docs/27, docs/11).
        feedback_auto_share: z.boolean(),
        // Whether app crash logs are sent to Shift automatically (ADR-0069). Off
        // means only an org-admin's Send to Shift on /feedback sends one.
        crash_log_auto_share: z.boolean(),
        // Whether the Claude Code plugin's observer hook files reports for this
        // org. It reads builder sessions locally, so it ships off and the change
        // is audited (docs/28, docs/11).
        feedback_observer_reports: z.boolean(),
        // Whether the Greenlight plugin sends each build session's token counts
        // to this install (docs/11 §Cost Management). Off by default; audited.
        agent_token_usage_reports: z.boolean(),
        // Cursor's Admin API key for reading Cursor sessions' token counts. Write-only:
        // the read says whether one is stored and how the last pass went, never the key.
        cursor_usage: CursorUsageSettingsSchema.optional(),
        // Which coding agents' guidance surfaces are shown (docs/28 §Agent setup,
        // ADR-0040) — presentation filtering only. Mirrored read-only into
        // `agent_setup` below since that section is visible to every role.
        supported_agents: z.array(AgentKindSchema).min(1),
        // The administrator Shift reaches about this install's trial and
        // subscription, named in the wizard and editable here afterwards
        // (docs/12 §Install contact). Null on an install that predates the
        // wizard step; the forwarder sends nothing until it is filled in.
        contact_name: z.string().nullable(),
        contact_email: z.string().nullable(),
        dns_https_history: z
            .array(z.object({
            step: z.enum(['zone_delegation', 'records_created', 'cert_issued', 'healthz']),
            ok: z.boolean(),
            at: z.string().nullable(),
        }))
            .optional(),
    })
        .optional(),
    identity: z
        .object({
        provider: z.enum(['entra', 'okta', 'google', 'oidc']),
        oidc_issuer: z.string().url(),
        // Generic OIDC only: the sign-in button label, when configured.
        display_name: z.string().optional(),
        client_id: z.string(),
        scopes: z.array(z.string()),
        last_tested_at: z.string().nullable(),
        // Reconfigure-form fields (docs/28 §/settings). The client secret never
        // leaves Key Vault. `role_groups` and `group_claim` drive role mapping for
        // Entra/Okta only; Google roles are managed in the dashboard (docs/10 §IdP
        // group → platform role mapping), so a Google identity carries neither.
        group_claim: z.string(),
        role_groups: z
            .object({
            org_admin: z.array(z.string()).optional(),
            admin: z.array(z.string()).optional(),
            user: z.array(z.string()).optional(),
        })
            .optional(),
        // Google only; empty means no Greenlight-side domain check (docs/10
        // §Google Workspace roles).
        hosted_domains: z.array(z.string()).optional(),
        // Entra only: whether login caches IdP group display names (docs/10 §IdP
        // group display names).
        resolve_group_names: z.boolean().optional(),
        // Resolved display names for the mapped identifiers above, keyed by
        // identifier. Presentation-only and partial by construction — an absent
        // key renders as its identifier.
        group_display_names: z.record(z.string(), z.string()).optional(),
    })
        .optional(),
    source_control: z
        .object({
        provider: z.literal('github'),
        installation_id: z.string(),
        account: z.string(),
        status: z.enum(['connected', 'suspended', 'error']),
        default_branch: z.string(),
        require_pr: z.boolean(),
        require_checks: z.boolean(),
        scanner_pins: z.array(z.object({ name: z.string(), version: z.string(), overridden: z.boolean() })),
    })
        .optional(),
    notifications: z
        .object({
        slack: z
            .object({
            enabled: z.boolean(),
            channel_hint: z.string().nullable(),
            /**
             * The most recent failed Slack send, so Settings can say a destination
             * that is switched on is not actually delivering. A short reason code,
             * never the webhook URL and never the message body.
             */
            last_failure: z.object({ at: z.string(), reason: z.string() }).nullable(),
        })
            .nullable(),
        webhook: z.string().url().nullable(),
        email_recipients: z.array(z.string()),
        categories: z.object({
            deploy_failures: z.boolean(),
            health_alerts: z.boolean(),
            health_alert_email: z.boolean(),
            permission_requests: z.boolean(),
            connection_requests: z.boolean(),
            weekly_digest: z.boolean(),
        }),
        // Whether this install resolved a real email transport. A single bit —
        // never the transport, host, or sender address — so Settings can say that
        // categories which are on still deliver nothing (docs/11 §Notification Delivery).
        email_delivery_configured: z.boolean(),
        // The install's cloud, for the steps that set up a sender there; every IT
        // role sees the no-sender warning, while `org` is org-admin only.
        cloud: z.enum(CLOUD_PROVIDERS),
        // Which sender the install uses, so a failure names the right fix: an
        // Azure-managed sender has no relay password to check.
        email_transport: z.enum(EMAIL_TRANSPORTS),
        // The latest notification email send, when it failed: a configured sender
        // that cannot send (a relay password never stored, a refused sign-in).
        // A later successful send clears it. A fixed reason, never a host or address.
        email_last_failure: z
            .object({ at: z.string(), reason: z.enum(EMAIL_FAILURE_REASONS) })
            .nullable(),
    })
        .optional(),
    // The unused-app lifecycle: one switch and the day counts it runs on (IT).
    app_lifecycle: z
        .object({
        enabled: z.boolean(),
        thresholds: z.object({
            inactive_days: z.number().int(),
            auto_suspend_days: z.number().int(),
        }),
    })
        .optional(),
    // The marketplace repo management surface (org-admin only): every persisted
    // marketplace and whether the GitHub App step is complete.
    marketplace: z
        .object({
        github_app_ready: z.boolean(),
        repositories: z.array(PluginMarketplaceStatusSchema),
    })
        .optional(),
    agent_setup: z.object({
        marketplace_repositories: z.array(PluginMarketplaceStatusSchema),
        sparse_path: z.string().nullable(),
        mcp_url: z.string().url(),
        org_slug: z.string(),
        // Which agents the Agent setup selector should list (docs/28 §Agent setup).
        // Read-only mirror of `org.supported_agents`; guaranteed present since this
        // is the only section visible to every role.
        supported_agents: z.array(AgentKindSchema).min(1),
        // True when this install has no public ingress. Absent means public, so a
        // control plane that predates the field keeps the existing client list.
        private_install: z.boolean().optional(),
        // Active org admins, so a viewer who cannot create a marketplace knows who
        // can (docs/28 §Agent setup). Absent from a control plane that predates it.
        org_admins: z
            .array(z.object({ display_name: z.string().nullable(), email: z.string() }))
            .optional(),
    }),
    // Where platform roles come from (docs/10 §IdP group → platform role mapping):
    // `idp` recomputes roles from groups at login, `greenlight` assigns them
    // in-product. Google is always `greenlight`. Surfaced at the top level (not
    // inside the org-admin-only `identity` block) because IT — `admin` as well as
    // `org_admin` — needs the mode to render the Users & RBAC surface (docs/28
    // §/users). Present once the IdP is configured; absent on a fresh install.
    role_source: RoleSourceSchema.optional(),
    // Where group memberships come from (docs/41 §When groups are
    // Greenlight-managed). Derived from the IdP type, independent of
    // `role_source`, and IT-visible for the same reason: /users describes group
    // membership differently depending on who owns it. Absent on a fresh install.
    group_source: GroupSourceSchema.optional(),
});
// The ZodType annotation erases the object schema's inferred type from the
// declaration emit, keeping it under TS7056's serialization limit.
export const OrgSettingsSchema = orgSettingsSchema;
// Writer schemas — each covers only the mutable subset of its section.
// Domains and install-set fields are read-only post-install (docs/12).
export const ORG_DISPLAY_NAME_MIN_LENGTH = 2;
export const ORG_DISPLAY_NAME_MAX_LENGTH = 255;
export const UpdateOrgSettingsSchema = z.object({
    display_name: z.string().trim().min(ORG_DISPLAY_NAME_MIN_LENGTH).max(ORG_DISPLAY_NAME_MAX_LENGTH),
});
/** Body of `PATCH /api/settings/app-discoverability` — the org-admin default for
 *  whether a newly registered app starts open to the org or restricted to the
 *  people its owner names (docs/42 §Default app access). Registration stamps
 *  `apps.access_restricted` from it; existing apps are untouched. */
export const UpdateFeedbackSharingSettingsSchema = z.object({
    auto_share: z.boolean(),
});
/** Body of `PATCH /api/settings/feedback-observer-reports` (docs/28). */
export const UpdateFeedbackObserverReportsSettingsSchema = z.object({
    enabled: z.boolean(),
});
/** Body of `PATCH /api/settings/agent-token-usage-reports` (docs/28). */
export const UpdateAgentTokenUsageReportsSettingsSchema = z.object({
    enabled: z.boolean(),
});
/**
 * Body of `PUT /api/settings/agent-token-usage/cursor-admin-key`: a Cursor team
 * Admin API key. Stored in the vault and never returned by any read.
 */
export const SetCursorAdminKeySchema = z
    .object({
    api_key: z
        .string()
        .trim()
        .min(8)
        .max(512)
        .regex(/^[\x21-\x7e]+$/, 'must be a single token with no spaces'),
})
    .strict();
/** Body of `PATCH /api/settings/crash-log-sharing` (ADR-0069). */
export const UpdateCrashLogSharingSettingsSchema = z.object({
    auto_share: z.boolean(),
});
export const UpdateAppDiscoverabilitySettingsSchema = z.object({
    discoverable_by_default: z.boolean(),
});
/** Body of `PATCH /api/settings/identity/group-names` — the Entra-only opt-in
 *  that lets login cache IdP group display names (docs/10 §IdP group display
 *  names). Presentation-only; enabling it needs prior tenant admin consent for
 *  the delegated Graph scope. */
export const UpdateGroupNameResolutionSchema = z.object({
    resolve_group_names: z.boolean(),
});
/** Body of `PATCH /api/settings/supported-agents` — org-admin edit of which
 *  coding agents' guidance surfaces the org shows (docs/28, ADR-0040). */
export const UpdateSupportedAgentsSchema = z.object({
    supported_agents: z.array(AgentKindSchema).min(1),
});
// IdP reconfiguration (provider swap, secrets, group mappings) is a full
// configuration, not a partial patch — its body is the contract's
// `ReconfigureIdentityBodySchema` (mirrors the install wizard's ConfigureIdp).
// ── Source-control lifecycle actions (docs/07 §Re-install semantics) ────────
/** On-demand managed-repo access check against the active installation. */
export const SourceControlVerifyResultSchema = z.object({
    installation_id: z.string(),
    org_handle: z.string(),
    /** Managed repos the installation can reach. */
    accessible: z.array(z.string()),
    /** Managed repos the installation cannot see (e.g. missing "select repositories" entries). */
    missing: z.array(z.string()),
    ok: z.boolean(),
});
export const RecreateGithubAppBodySchema = z.object({
    /** GitHub org to create the replacement App under; defaults to the connected org. */
    github_org: z.string().optional(),
});
/**
 * The manifest round-trip fields the install wizard's `beginGithubApp` and
 * Settings' `recreateGithubApp` both hand to `submitManifestForm` (docs/07
 * §GitHub App creation flow). One owner so the two results can't drift:
 * `beginGithubApp` composes this partially (absent when `alreadyInstalled`),
 * `recreateGithubApp` always has it.
 */
export const GithubAppManifestHandshakeSchema = z.object({
    manifest: z.string(),
    action: z.string().url(),
    state: z.string(),
});
/** Manifest round for App re-creation — same form-submission shape the wizard uses. */
export const RecreateGithubAppResultSchema = GithubAppManifestHandshakeSchema.extend({
    previous_app_slug: z.string(),
});
export const ScmMigrationRepoMoveSchema = z.object({
    resource_kind: z.enum(['app', 'plugin_marketplace']),
    resource_id: z.string().uuid(),
    resource_name: z.string(),
    from: z.string(),
    to: z.string(),
});
/** Dry-run listing of exactly what an org migration will re-point. */
export const ScmMigrationPreviewSchema = z.object({
    current: z.object({ org_handle: z.string(), installation_id: z.string() }),
    target: z.object({
        org_handle: z.string(),
        installation_id: z.string(),
        seen_at: z.string(),
    }),
    repos: z.array(ScmMigrationRepoMoveSchema),
});
export const ExecuteScmMigrationBodySchema = z.object({
    /** Must match the pending target's handle — an explicit confirmation, not a hint. */
    target_org_handle: z.string().min(1),
});
export const ScmMigrationPostCheckSchema = z.object({
    repo_full_name: z.string(),
    branch_protection_applied: z.boolean(),
    workflow_present: z.boolean(),
    error: z.string().optional(),
});
export const ScmMigrationResultSchema = z.object({
    migrated: z.boolean(),
    org_handle: z.string(),
    installation_id: z.string(),
    repos_moved: z.number().int(),
    /** Per-app-repo branch-protection / pipeline-workflow checks after the re-point. */
    post_checks: z.array(ScmMigrationPostCheckSchema),
});
// Persisted shape of `orgs.notification_settings` (docs/28 §/settings). The read
// API (`OrgSettingsSchema.notifications`) re-adds the non-configurable
// `categories.deploy_failures: false`, which is never stored here.
/**
 * A manual updater run the dashboard or an agent asked for (docs/26 §Invocation
 * Paths). `canary` previews — discover, verify, preflight, plan-only, apply
 * nothing; `apply` runs what the schedule would have run.
 */
export const UpdaterRunModeSchema = z.enum(['apply', 'canary']);
export const TriggerUpdaterRunRequestSchema = z.object({ mode: UpdaterRunModeSchema });
/**
 * The Job the control plane created by cloning the live CronJob template. The
 * run record itself arrives through the updater's own status writes, so this
 * carries only what the request produced.
 */
export const TriggerUpdaterRunResponseSchema = z.object({
    job_name: z.string(),
    mode: UpdaterRunModeSchema,
});
/**
 * Updater-runs list query. Omitting the filter includes every run, so an
 * existing caller's pages are unchanged.
 *
 * Literals rather than `z.coerce.boolean()`, which reads the non-empty string
 * `"false"` as true.
 */
export const UpdaterRunsQuerySchema = PaginationQuerySchema.extend({
    include_up_to_date: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
/**
 * A configured Slack incoming-webhook destination. The URL itself is a bearer
 * credential — anyone holding it can post to the channel — so it lives in the
 * vault and never in this JSONB. A non-null value here means one is stored.
 */
export const SlackDestinationSchema = z.object({
    enabled: z.boolean(),
    /** What IT typed, shown so the row is recognisable. Never used to route. */
    channel_hint: z.string().nullable(),
});
export const NotificationSettingsSchema = z.object({
    slack: SlackDestinationSchema.nullable(),
    // Reserved, not a destination: no generic-webhook transport exists. Kept in the
    // read shape so rows an earlier dashboard wrote still parse, and absent from the
    // update shape below so nothing can newly configure one.
    webhook: z.string().url().nullable(),
    email_recipients: z.array(z.string()),
    categories: z.object({
        health_alerts: z.boolean(),
        // Whether health alerts are also emailed, not just posted to Slack. A channel
        // switch inside `health_alerts` rather than a category of its own; off by
        // default (migration 0089).
        health_alert_email: z.boolean(),
        permission_requests: z.boolean(),
        // Connecting a new upstream source is a different decision from widening
        // access to one already connected, so it is its own opt-in (ADR-0026 §7).
        connection_requests: z.boolean(),
        // The weekly roll-up: the only category that reaches administrators on a
        // schedule rather than in response to something someone did.
        weekly_digest: z.boolean(),
    }),
});
export const UpdateNotificationsSettingsSchema = z
    .object({
    // Toggling an already-configured destination, and nothing else. Forgetting
    // one goes through DELETE /api/settings/notifications/slack-webhook, which
    // is the only path that also removes the vault secret — clearing the row
    // here would leave a live webhook URL behind with the UI saying there is
    // none. The URL itself arrives on its own write-only route.
    slack: z.object({ enabled: z.boolean() }),
    email_recipients: z.array(z.string()),
    categories: z
        .object({
        health_alerts: z.boolean(),
        health_alert_email: z.boolean(),
        permission_requests: z.boolean(),
        connection_requests: z.boolean(),
        weekly_digest: z.boolean(),
    })
        .partial(),
})
    .partial();
/**
 * The write-only Slack webhook registration. Restricted to Slack's own hooks
 * host: this URL is where the control plane posts org data, so an arbitrary
 * origin here would turn Settings into an exfiltration primitive.
 */
export const SetSlackWebhookSchema = z.object({
    webhook_url: z
        .string()
        .url()
        .refine((u) => {
        try {
            const parsed = new URL(u);
            return parsed.protocol === 'https:' && parsed.hostname === 'hooks.slack.com';
        }
        catch {
            return false;
        }
    }, 'must be an https://hooks.slack.com/ incoming-webhook URL'),
    channel_hint: z.string().max(80).nullable().optional(),
});
/**
 * One IdP verification check (docs/12 §IdP OIDC App Registration).
 * `inconclusive` is an outcome, not a pending state: the probe ran — or cannot
 * run against this provider at all — and the answer is not attributable, so it
 * is reported as a check that did not run and never blocks a save. Only `fail`
 * blocks, and `detail`/`suggested_fix` are operator-facing text, never the
 * IdP's raw response.
 */
export const IdpCheckResultSchema = z.object({
    status: z.enum(['pass', 'fail', 'inconclusive']),
    detail: z.string(),
    /** Where the correct value is copied from; shown on a failing row. */
    suggested_fix: z.string().optional(),
    category: z.enum(['blocked', 'mismatch', 'not_ready']).optional(),
});
/**
 * The discovery checks (the issuer document and the endpoints login consumes)
 * plus the client-registration checks that exercise the entered client ID,
 * secret, and redirect URI.
 */
export const IdpVerificationChecksSchema = z.object({
    /** Discovery document fetched and its `issuer` matches the supplied URL. */
    issuer: IdpCheckResultSchema,
    authorization_endpoint: IdpCheckResultSchema,
    token_endpoint: IdpCheckResultSchema,
    jwks_uri: IdpCheckResultSchema,
    /** Client ID against the provider's known shape; no network call. */
    client_id_format: IdpCheckResultSchema,
    /** Client-credentials request to the discovered `token_endpoint`. */
    client_credentials: IdpCheckResultSchema,
    /** Authorize pre-flight with the redirect URI the login flow builds. */
    redirect_uri: IdpCheckResultSchema,
});
export const TestIdentityResultSchema = z.object({
    /** True when no check failed — an inconclusive check does not block. */
    ok: z.boolean(),
    message: z.string(),
    tested_at: z.string(),
    checks: IdpVerificationChecksSchema,
});
// ── Feedback channel — see 15-database-schema.md §feedback, 28-it-dashboard-ui.md
// §/feedback, and T-986. `source`/`surface` are server-derived (never
// client-claimed); the vocabulary + caps live in ../types/feedback.js so the
// contract, the MCP tool, and the server service all validate identically.
export const FeedbackSourceSchema = z.enum(FEEDBACK_SOURCES);
export const FeedbackSurfaceSchema = z.enum(FEEDBACK_SURFACES);
export const FeedbackCategorySchema = z.enum(FEEDBACK_CATEGORIES);
export const FeedbackStatusSchema = z.enum(FEEDBACK_STATUSES);
// Source-specific context envelopes. `.strict()` rejects unknown keys so context
// stays an allowlist, not free-form JSON growth (T-986 AC-FEEDBACK-03). All
// fields optional — the capturing surface fills what it can.
export const AgentFeedbackContextSchema = z
    .object({
    agent_runtime: z.string().max(120).optional(),
    os: z.string().max(120).optional(),
    plugin_version: z.string().max(60).optional(),
    request_ids: z.array(z.string().max(64)).max(FEEDBACK_CAPS.maxRequestIds).optional(),
    origin: z.enum(FEEDBACK_ORIGINS).optional(),
    fingerprint: z.string().regex(FEEDBACK_FINGERPRINT_PATTERN).optional(),
})
    .strict();
export const UserFeedbackContextSchema = z
    .object({
    route: z.string().max(512).optional(),
    user_agent: z.string().max(512).optional(),
    viewport: z.string().max(32).optional(),
})
    .strict();
export const FeedbackTitleSchema = z.string().min(1).max(FEEDBACK_CAPS.titleMaxChars);
export const FeedbackBodySchema = z.string().min(1).max(FEEDBACK_CAPS.bodyMaxChars);
/** Coding-agent clients whose build sessions report their own token counts. */
export const AGENT_TOKEN_USAGE_CLIENTS = ['claude-code', 'codex'];
/**
 * Bounds on one session report. The schema refuses a malformed body with 400;
 * these refuse an implausible one with 422, since the counts are self-reported.
 */
export const AGENT_TOKEN_USAGE_LIMITS = {
    maxUsageRows: 32,
    maxTokensPerSession: 10_000_000_000,
    maxClockSkewMs: 5 * 60 * 1000,
};
const tokenCount = z.number().int().nonnegative();
/**
 * Body of `POST /api/cli/token-usage` (docs/45): one session's running totals
 * per model, counts only. Strict, so a prompt, path, or transcript fragment in
 * any field the schema does not name is refused.
 */
export const AgentTokenUsageReportSchema = z
    .object({
    app_id: z.string().uuid(),
    client: z.enum(AGENT_TOKEN_USAGE_CLIENTS),
    client_session_id: z.string().min(1).max(200),
    started_at: z.string().datetime({ offset: true }),
    ended_at: z.string().datetime({ offset: true }),
    usage: z
        .array(z
        .object({
        model: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:@[\]/-]{0,127}$/),
        variant: z.enum(AGENT_USAGE_VARIANTS).optional(),
        input_tokens: tokenCount,
        output_tokens: tokenCount,
        cache_write_5m_tokens: tokenCount,
        cache_write_1h_tokens: tokenCount,
        cache_read_tokens: tokenCount,
    })
        .strict())
        .min(1)
        .max(AGENT_TOKEN_USAGE_LIMITS.maxUsageRows)
        .refine((rows) => new Set(rows.map((r) => `${r.model}|${r.variant ?? 'standard'}`)).size === rows.length, { message: 'Each model and variant appears once per report.' }),
})
    .strict();
/**
 * Body of `POST /api/cli/token-usage/cursor-session` (docs/45): a builder's
 * Cursor hook claims one conversation for the app in its workspace. It carries
 * no counts; the control plane reads those from Cursor's Admin API and keeps
 * only usage events whose `conversationId` matches a claim by the same person.
 */
export const CursorSessionClaimSchema = z
    .object({
    app_id: z.string().uuid(),
    conversation_id: z.string().min(1).max(200),
    started_at: z.string().datetime({ offset: true }),
    ended_at: z.string().datetime({ offset: true }),
})
    .strict();
//# sourceMappingURL=api.js.map